import { Component, HostBinding, inject, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { FormService, PropertyFormModel } from '@app/services/form.service';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { FormUtils } from '@app/utils/form-utils';
import { Property } from '@app/class/property.class';
import { RuianService } from '@app/services/ruian.service';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { AddressInterface } from '@app/interfaces/address.interface';
import { SnackbarService } from '@app/services/snackbar.service';
import { Subject } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { HasErrorDirective } from '@app/directives/validation/has-error.directive';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { InfoPanelComponent } from '@app/components/info-panel/info-panel.component';
import { HypoRadioComponent } from '@app/components/form-components/hypo-radio/hypo-radio.component';
import { HypoInputComponent } from '@app/components/form-components/hypo-input/hypo-input.component';
import { TooltipComponent } from '@app/components/tooltip/tooltip.component';
import { ShouldShowInputPipe } from '@app/pipes/should-show-input.pipe';
import { ListByTypePipe } from '@app/pipes/list-by-type-pipe/list-by-type.pipe';
import { OptionPipe } from '@app/pipes/option-pipe/option.pipe';
import { AsyncPipe } from '@angular/common';
import { RichFormControlDirective } from '@app/directives/validation/rich-form-control.directive';
import { MortgageInterface } from '@app/interfaces/mortgage.interface';

@Component({
    selector: 'app-property-dialog',
    templateUrl: './property-dialog.component.html',
    styleUrls: ['./property-dialog.component.scss'],
    imports: [
        MatIconModule,
        MatDialogModule,
        ListByTypePipe,
        ReactiveFormsModule,
        HasErrorDirective,
        RichFormControlDirective,
        MatInputModule,
        MatSlideToggleModule,
        MatButtonModule,
        MatAutocompleteModule,
        InfoPanelComponent,
        HypoRadioComponent,
        HypoInputComponent,
        TooltipComponent,
        ShouldShowInputPipe,
        OptionPipe,
        AsyncPipe,
    ],
})
export class PropertyDialogComponent implements OnInit, OnDestroy {
    dialogRef = inject<MatDialogRef<PropertyDialogComponent>>(MatDialogRef);
    data = inject(MAT_DIALOG_DATA);

    protected readonly formService = inject(FormService);
    public readonly ruianService = inject(RuianService);
    public readonly snackbarService = inject(SnackbarService);

    protected destroyed$: Subject<void> = new Subject<void>();

    public isSent: boolean = false;
    public propertyMethodicIsValid: boolean = false;
    public propertyDialogForm: UntypedFormGroup;
    @HostBinding('class.show-more') public showMore: boolean = false;

    public hasSpecificProperty: boolean = false;

    public property: PropertyFormModel;
    public properties: PropertyFormModel[];

    public mortgage: MortgageInterface = {};
    public propertyConfig = new Property();

    get type() {
        return this.propertyDialogForm.get('type') as UntypedFormArray;
    }

    public close(data?: any) {
        this.dialogRef.close(data);
    }

    ngOnInit() {
        this.showMore = this.data?.showMore;
        this.property = this.data?.property?.getRawValue();
        this.properties = this.data?.properties?.getRawValue();
        this.mortgage = this.data?.mortgage;
        this.propertyDialogForm = this.formService.createPropertyForm(
            this.property ?? {},
            this.properties,
            this.mortgage?.properties
        );
        this.bindValueChangesDetection();
        this.checkIfPropertyIsValid();
    }

    ngOnDestroy(): void {
        this.destroyed$?.next();
        this.destroyed$?.complete();
    }

    public isRequiredByType(inputName: string): boolean {
        return FormUtils.isRequiredByType(this.propertyConfig.requiredOnTypes, inputName, this.type.value);
    }

    public continue(): void {
        this.isSent = true;
        if (this.formService.hasValidVisibleControls(this.propertyDialogForm, true) && this.checkIfPropertyIsValid()) {
            const commercialAreaPercentageValue = this.propertyDialogForm.get('commercialAreaPercentage').value;
            if (commercialAreaPercentageValue) {
                this.propertyDialogForm
                    .get('commercialAreaPercentage')
                    .patchValue(FormUtils.unmaskPercent(commercialAreaPercentageValue));
            }
            this.close(this.propertyDialogForm);
        }
    }

    public getAddressObject($event: MatAutocompleteSelectedEvent) {
        this.ruianService.getObject($event.option.value).then((ruianAddress: AddressInterface) => {
            if (ruianAddress) {
                this.propertyDialogForm.get(['address']).patchValue(ruianAddress, { emitEvent: false });
            } else {
                this.propertyDialogForm
                    .get(['address', 'street'])
                    .patchValue($event.option.viewValue ?? '', { emitEvent: false });
            }
        });
        return false;
    }

    public pickAddressFromApplicant(addressPropertyKey: string, applicantIndex: number = 0): void {
        const addressToFill = this.mortgage._contacts[applicantIndex].address?.[addressPropertyKey];
        if (addressToFill && !addressToFill.orientationNumber) {
            addressToFill.orientationNumber = null;
        }
        if (addressToFill) {
            this.propertyDialogForm.get('address').patchValue(addressToFill, { emitEvent: false });
        }
    }

    protected bindValueChangesDetection() {
        const commercialAreaPercentageValue = this.propertyDialogForm.get('commercialAreaPercentage').value;
        if (commercialAreaPercentageValue) {
            this.propertyDialogForm
                .get('commercialAreaPercentage')
                .patchValue(FormUtils.maskPercent(commercialAreaPercentageValue));
        }

        this.propertyDialogForm
            .get('type')
            .valueChanges.pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.formService.rebuildValidatorsOnCustomInputs('street');
                this.formService.rebuildValidatorsOnCustomInputs('registryNumber');
                this.formService.rebuildValidatorsOnCustomInputs('cadastralTerritory');
                this.formService.rebuildValidatorsOnCustomInputs('isDeveloperProject');
            });

        this.propertyDialogForm
            .get('isDeveloperProject')
            .valueChanges.pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.formService.rebuildValidatorsOnCustomInputs('registryNumber');
            });

        this.propertyDialogForm
            .get('address.street')
            .valueChanges.pipe(
                takeUntil(this.destroyed$),
                debounceTime(400),
                distinctUntilChanged(),
                filter((value) => value?.length > 1)
            )
            .subscribe((value: string) => {
                this.ruianService.findAddress(value);
            });

        this.propertyDialogForm
            .get('address.city')
            .valueChanges.pipe(
                takeUntil(this.destroyed$),
                debounceTime(400),
                distinctUntilChanged(),
                filter((value) => value?.length > 1)
            )
            .subscribe((value: string) => {
                this.ruianService.findCity(value);
            });

        this.propertyDialogForm
            .get('isFunded')
            .valueChanges.pipe(takeUntil(this.destroyed$))
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .subscribe((value: string) => {
                this.checkIfPropertyIsValid();
            });

        this.propertyDialogForm
            .get('isPledged')
            .valueChanges.pipe(takeUntil(this.destroyed$))
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .subscribe((value: string) => {
                this.checkIfPropertyIsValid();
            });

        this.propertyDialogForm
            .get('isOwned')
            .valueChanges.pipe(takeUntil(this.destroyed$))
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .subscribe((value: string) => {
                this.checkIfPropertyIsValid();
            });
    }

    private checkIfPropertyIsValid(): boolean {
        this.propertyMethodicIsValid = true;
        if (
            !this.propertyDialogForm.get('isOwned').value &&
            !this.propertyDialogForm.get('isPledged').value &&
            !this.propertyDialogForm.get('isFunded').value
        ) {
            this.propertyMethodicIsValid = false;
        }
        return this.propertyMethodicIsValid;
    }
}
